import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  getBbpsStats, getBbpsApiBalance, getAllBbpsTransactions, listBbpsWallets,
  searchBbpsUsers, bbpsWalletTopup, bbpsApiTopup, getBbpsApiLedger,
  listBbpsAccess, grantBbpsAccess, revokeBbpsAccess,
} from '../lib/api';
import BbpsReportModal from '../components/bbps/BbpsReportModal';

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 0 })}`;
const fmtDT = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

const STATUS_STYLES = {
  success: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  failed: 'bg-red-100 text-red-700 border-red-300',
  pending: 'bg-amber-100 text-amber-700 border-amber-300',
};

export default function BbpsAdminPage() {
  const [tab, setTab] = useState('overview');  // overview | wallets | transactions | ledger | access | integration
  const [stats, setStats] = useState(null);
  const [apiBal, setApiBal] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [walletSearch, setWalletSearch] = useState('');
  const [txns, setTxns] = useState([]);
  const [txnFilter, setTxnFilter] = useState({ status: '', category: '' });
  const [apiLedger, setApiLedger] = useState([]);

  // Access Control state
  const [granted, setGranted] = useState([]);
  const [accessSearchUsers, setAccessSearchUsers] = useState([]);
  const [accessSearchTerm, setAccessSearchTerm] = useState('');
  const [selectedGrant, setSelectedGrant] = useState(new Set());
  const [grantNote, setGrantNote] = useState('');

  // Wallet topup modal
  const [wtOpen, setWtOpen] = useState(false);
  const [wtUsers, setWtUsers] = useState([]);
  const [wtSearch, setWtSearch] = useState('');
  const [wtUserId, setWtUserId] = useState('');
  const [wtAmount, setWtAmount] = useState('');
  const [wtNote, setWtNote] = useState('');
  const [wtBusy, setWtBusy] = useState(false);

  // API topup modal
  const [apiOpen, setApiOpen] = useState(false);
  const [apiAmount, setApiAmount] = useState('');
  const [apiNote, setApiNote] = useState('');
  const [apiBusy, setApiBusy] = useState(false);

  // Report drill-down modal
  const [reportKind, setReportKind] = useState(null);


  const loadStats = useCallback(async () => {
    try {
      const [s, b] = await Promise.all([getBbpsStats(), getBbpsApiBalance()]);
      setStats(s.data); setApiBal(b.data);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Stats fail'); }
  }, []);

  const loadWallets = useCallback(async () => {
    try {
      const res = await listBbpsWallets(walletSearch || undefined);
      setWallets(res.data.wallets || []);
    } catch { /* ignore */ }
  }, [walletSearch]);

  const loadTxns = useCallback(async () => {
    try {
      const res = await getAllBbpsTransactions({ limit: 200, ...(txnFilter.status ? { status: txnFilter.status } : {}), ...(txnFilter.category ? { category: txnFilter.category } : {}) });
      setTxns(res.data.transactions || []);
    } catch { /* ignore */ }
  }, [txnFilter]);

  const loadApiLedger = useCallback(async () => {
    try {
      const res = await getBbpsApiLedger(200);
      setApiLedger(res.data.entries || []);
    } catch { /* ignore */ }
  }, []);

  const loadGranted = useCallback(async () => {
    try {
      const res = await listBbpsAccess();
      setGranted(res.data.granted || []);
    } catch { /* ignore */ }
  }, []);

  const searchAccessUsers = useCallback(async (term = '') => {
    try {
      const res = await searchBbpsUsers(term || undefined);
      // Exclude already-granted from the picker
      const grantedIds = new Set(granted.map(g => g.user_id));
      setAccessSearchUsers((res.data.users || []).filter(u => !grantedIds.has(u.id)));
    } catch { /* ignore */ }
  }, [granted]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { if (tab === 'wallets') loadWallets(); }, [tab, loadWallets]);
  useEffect(() => { if (tab === 'transactions') loadTxns(); }, [tab, loadTxns]);
  useEffect(() => { if (tab === 'ledger') loadApiLedger(); }, [tab, loadApiLedger]);
  useEffect(() => { if (tab === 'access') { loadGranted(); } }, [tab, loadGranted]);
  useEffect(() => { if (tab === 'access' && granted) searchAccessUsers(''); }, [tab, granted, searchAccessUsers]);

  const toggleGrant = (uid) => {
    setSelectedGrant((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const doGrantSelected = async () => {
    const ids = Array.from(selectedGrant);
    if (ids.length === 0) { toast.error('Kam se kam 1 user select karein'); return; }
    try {
      const res = await grantBbpsAccess({ user_ids: ids, notes: grantNote });
      toast.success(`${res.data.granted}/${res.data.requested} users ko access mila`);
      setSelectedGrant(new Set());
      setGrantNote('');
      await loadGranted();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Grant fail'); }
  };

  const doRevoke = async (uid, name) => {
    if (!window.confirm(`${name || uid} ka BBPS access revoke karein?`)) return;
    try {
      await revokeBbpsAccess(uid);
      toast.success('Access revoked');
      await loadGranted();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Revoke fail'); }
  };

  const openWtModal = async (userId = '') => {
    setWtUserId(userId); setWtAmount(''); setWtNote(''); setWtSearch(''); setWtOpen(true);
    try { const res = await searchBbpsUsers(); setWtUsers(res.data.users || []); } catch { /* ignore */ }
  };

  const doSearchUsers = async () => {
    try { const res = await searchBbpsUsers(wtSearch || undefined); setWtUsers(res.data.users || []); }
    catch { /* ignore */ }
  };

  const doWtSubmit = async () => {
    if (!wtUserId) { toast.error('User select karein'); return; }
    const amt = parseFloat(wtAmount);
    if (!amt || amt <= 0) { toast.error('Valid amount daalein'); return; }
    try {
      setWtBusy(true);
      const res = await bbpsWalletTopup({ user_id: wtUserId, amount: amt, note: wtNote });
      toast.success(`Credited ${fmt(amt)} → new balance ${fmt(res.data.new_balance)}`);
      setWtOpen(false);
      await loadStats();
      if (tab === 'wallets') await loadWallets();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Topup fail'); }
    finally { setWtBusy(false); }
  };

  const doApiTopup = async () => {
    const amt = parseFloat(apiAmount);
    if (!amt || amt <= 0) { toast.error('Valid amount'); return; }
    try {
      setApiBusy(true);
      const res = await bbpsApiTopup({ amount: amt, note: apiNote });
      toast.success(`API balance loaded → ${fmt(res.data.new_balance)}`);
      setApiOpen(false); setApiAmount(''); setApiNote('');
      await loadStats();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Topup fail'); }
    finally { setApiBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 lg:p-6" data-testid="bbps-admin-page">
      <div className="max-w-7xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <span className="text-3xl">🏦</span> BBPS Admin Control
            </h1>
            <p className="text-sm text-gray-600">Wallet top-ups · API balance · Transaction ledger</p>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="btn-open-wallet-topup"
              onClick={() => openWtModal('')}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium shadow"
            >
              + Credit User Wallet
            </button>
            <button
              data-testid="btn-open-api-topup"
              onClick={() => setApiOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium shadow"
            >
              + Load API Balance
            </button>
          </div>
        </div>

        {/* Stat tiles */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatTile label="API Balance" value={fmt(stats.api_balance)} color="emerald" sub={`Loaded ${fmt(stats.api_lifetime_topup)}`} testid="stat-api-balance" onClick={() => setReportKind('api_balance')} />
            <StatTile label="Wallet Balance (all users)" value={fmt(stats.total_wallet_balance)} color="purple" sub={`${stats.total_wallets} wallets`} testid="stat-wallet-total" onClick={() => setReportKind('wallets')} />
            <StatTile label="Total Volume" value={fmt(stats.total_volume)} color="blue" sub={`${stats.total_success} success txns`} testid="stat-volume" onClick={() => setReportKind('volume')} />
            <StatTile label="Success Rate" value={`${(stats.success_rate || 0).toFixed(1)}%`} color="orange" sub={`${stats.total_failed} failed`} testid="stat-success-rate" onClick={() => setReportKind('success_rate')} />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-white p-1 rounded-lg shadow-sm border w-fit flex-wrap" data-testid="bbps-admin-tabs">
          {[
            { k: 'overview', label: '📊 Overview' },
            { k: 'wallets', label: '👛 Wallets' },
            { k: 'transactions', label: '💸 Transactions' },
            { k: 'ledger', label: '📜 API Ledger' },
            { k: 'access', label: '🔐 Access Control' },
            { k: 'integration', label: '🔌 Integration Guide' },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`tab-${t.k}`}
              className={`px-3 py-1.5 rounded text-sm font-medium transition ${
                tab === t.k ? 'bg-purple-600 text-white shadow' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {tab === 'overview' && apiBal && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-base font-semibold mb-3">Aggregator (BBPS) Balance</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <MetricRow label="Current" value={fmt(apiBal.balance)} bold />
              <MetricRow label="Total Loaded" value={fmt(apiBal.lifetime_topup)} />
              <MetricRow label="Total Drained" value={fmt(apiBal.lifetime_drained)} />
              <MetricRow label="Provider" value={apiBal.provider || 'MOCK'} />
              <MetricRow label="Last Updated" value={fmtDT(apiBal.updated_at)} />
            </div>
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-800">
              🚧 <b>Mock provider</b> — jab actual BBPS aggregator approval mile (Setu / EuroNet / Cashfree), <code className="bg-white px-1 rounded font-mono">/app/backend/routes/bbps.py</code> ke <code className="bg-white px-1 rounded font-mono">_mock_pay_bill</code> aur <code className="bg-white px-1 rounded font-mono">_mock_fetch_bill</code> ko real HTTP call se swap kar dena. Response schema bilkul same rahega — koi frontend change nahi.
            </div>
          </div>
        )}

        {tab === 'wallets' && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-3 border-b flex gap-2 items-center flex-wrap">
              <input
                data-testid="wallet-search"
                type="text"
                value={walletSearch}
                onChange={(e) => setWalletSearch(e.target.value)}
                placeholder="Search user by name/email..."
                className="border rounded px-3 py-1.5 text-sm flex-1 min-w-[200px]"
              />
              <button onClick={loadWallets} className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded text-sm">Search</button>
              <span className="text-xs text-gray-500 ml-auto">{wallets.length} wallets</span>
            </div>
            <div className="overflow-x-auto max-h-[600px]">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-[10px] uppercase text-gray-600">
                    <th className="px-3 py-2 text-left">User</th>
                    <th className="px-3 py-2 text-left">Role</th>
                    <th className="px-3 py-2 text-right">Balance</th>
                    <th className="px-3 py-2 text-right">Credited</th>
                    <th className="px-3 py-2 text-right">Debited</th>
                    <th className="px-3 py-2 text-left">Updated</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {wallets.map((w) => (
                    <tr key={w.user_id} className="border-t hover:bg-slate-50" data-testid={`wallet-row-${w.user_id}`}>
                      <td className="px-3 py-2">
                        <p className="font-medium">{w.name || '—'}</p>
                        <p className="text-[10px] text-gray-500">{w.email}</p>
                      </td>
                      <td className="px-3 py-2 text-xs uppercase text-gray-600">{w.role}</td>
                      <td className="px-3 py-2 text-right font-bold">{fmt(w.balance)}</td>
                      <td className="px-3 py-2 text-right text-emerald-700">{fmt(w.lifetime_credited)}</td>
                      <td className="px-3 py-2 text-right text-red-700">{fmt(w.lifetime_debited)}</td>
                      <td className="px-3 py-2 text-xs text-gray-500">{fmtDT(w.updated_at)}</td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => openWtModal(w.user_id)}
                          data-testid={`btn-topup-${w.user_id}`}
                          className="text-xs bg-purple-100 hover:bg-purple-200 text-purple-800 px-2 py-1 rounded"
                        >
                          + Topup
                        </button>
                      </td>
                    </tr>
                  ))}
                  {wallets.length === 0 && (
                    <tr><td colSpan={7} className="text-center p-6 text-gray-500">Koi wallet nahi mila</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'transactions' && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-3 border-b flex gap-2 items-center flex-wrap">
              <select
                value={txnFilter.status}
                onChange={(e) => setTxnFilter({ ...txnFilter, status: e.target.value })}
                className="border rounded px-2 py-1 text-sm"
              >
                <option value="">All Status</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
              </select>
              <select
                value={txnFilter.category}
                onChange={(e) => setTxnFilter({ ...txnFilter, category: e.target.value })}
                className="border rounded px-2 py-1 text-sm"
              >
                <option value="">All Categories</option>
                <option value="mobile_prepaid">Mobile Prepaid</option>
                <option value="mobile_postpaid">Mobile Postpaid</option>
                <option value="dth">DTH</option>
                <option value="electricity">Electricity</option>
                <option value="gas">Gas</option>
                <option value="fastag">FASTag</option>
                <option value="water">Water</option>
                <option value="broadband">Broadband</option>
                <option value="credit_card">Credit Card</option>
                <option value="loan">Loan</option>
                <option value="insurance">Insurance</option>
              </select>
              <span className="text-xs text-gray-500 ml-auto">{txns.length} txns</span>
            </div>
            <div className="overflow-x-auto max-h-[600px]">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-[10px] uppercase text-gray-600">
                    <th className="px-2 py-2 text-left">Date</th>
                    <th className="px-2 py-2 text-left">User</th>
                    <th className="px-2 py-2 text-left">Biller</th>
                    <th className="px-2 py-2 text-left">Customer</th>
                    <th className="px-2 py-2 text-right">Amount</th>
                    <th className="px-2 py-2 text-left">Status</th>
                    <th className="px-2 py-2 text-left">BBPS Ref</th>
                  </tr>
                </thead>
                <tbody>
                  {txns.map((t) => (
                    <tr key={t.id} className="border-t hover:bg-slate-50">
                      <td className="px-2 py-1.5 text-gray-600 whitespace-nowrap">{fmtDT(t.created_at)}</td>
                      <td className="px-2 py-1.5">{t.user_name}</td>
                      <td className="px-2 py-1.5"><span className="font-medium">{t.biller_name}</span></td>
                      <td className="px-2 py-1.5 font-mono">{t.customer_id}</td>
                      <td className="px-2 py-1.5 text-right font-semibold">{fmt(t.amount)}</td>
                      <td className="px-2 py-1.5">
                        <span className={`border rounded px-1.5 py-0.5 text-[9px] uppercase ${STATUS_STYLES[t.status] || ''}`}>{t.status}</span>
                      </td>
                      <td className="px-2 py-1.5 font-mono text-[10px] text-gray-600">{t.bbps_ref_no || '—'}</td>
                    </tr>
                  ))}
                  {txns.length === 0 && (
                    <tr><td colSpan={7} className="text-center p-6 text-gray-500">Koi transaction nahi</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'ledger' && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-3 border-b text-sm text-gray-600">API Balance ke saare credit/debit entries (top-ups + txn drains)</div>
            <div className="overflow-x-auto max-h-[600px]">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-[10px] uppercase text-gray-600">
                    <th className="px-2 py-2 text-left">Date</th>
                    <th className="px-2 py-2 text-left">Type</th>
                    <th className="px-2 py-2 text-right">Amount</th>
                    <th className="px-2 py-2 text-right">Balance After</th>
                    <th className="px-2 py-2 text-left">Source</th>
                    <th className="px-2 py-2 text-left">Note</th>
                    <th className="px-2 py-2 text-left">Ref Txn</th>
                  </tr>
                </thead>
                <tbody>
                  {apiLedger.map((e) => (
                    <tr key={e.id} className="border-t hover:bg-slate-50">
                      <td className="px-2 py-1.5 text-gray-600 whitespace-nowrap">{fmtDT(e.created_at)}</td>
                      <td className="px-2 py-1.5">
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase ${e.type === 'credit' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{e.type}</span>
                      </td>
                      <td className={`px-2 py-1.5 text-right font-semibold ${e.type === 'credit' ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(e.amount)}</td>
                      <td className="px-2 py-1.5 text-right">{fmt(e.balance_after)}</td>
                      <td className="px-2 py-1.5 text-xs">{e.source}</td>
                      <td className="px-2 py-1.5 text-xs">{e.note}</td>
                      <td className="px-2 py-1.5 font-mono text-[10px]">{e.ref_txn_id || '—'}</td>
                    </tr>
                  ))}
                  {apiLedger.length === 0 && <tr><td colSpan={7} className="text-center p-6 text-gray-500">Koi entry nahi</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Access Control tab */}
        {tab === 'access' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="bbps-access-panel">
            {/* Currently granted */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="px-3 py-2 border-b bg-emerald-50 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-emerald-900">✓ Access Granted ({granted.length})</h3>
              </div>
              <div className="max-h-[500px] overflow-y-auto">
                {granted.length === 0 ? (
                  <p className="p-6 text-center text-sm text-gray-500">Abhi tak kisi ko access nahi diya. Right side se select karke Grant karo.</p>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr className="text-[10px] uppercase text-gray-600">
                        <th className="px-2 py-1.5 text-left">User</th>
                        <th className="px-2 py-1.5 text-left">Role</th>
                        <th className="px-2 py-1.5 text-left">Granted By</th>
                        <th className="px-2 py-1.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {granted.map((g) => (
                        <tr key={g.user_id} className="border-t hover:bg-emerald-50/50" data-testid={`granted-${g.user_id}`}>
                          <td className="px-2 py-1.5">
                            <p className="font-medium">{g.name || '—'}</p>
                            <p className="text-[10px] text-gray-500">{g.email}</p>
                          </td>
                          <td className="px-2 py-1.5 text-xs uppercase text-gray-600">{g.role}</td>
                          <td className="px-2 py-1.5 text-[10px] text-gray-600">
                            <p>{g.granted_by || '—'}</p>
                            <p className="text-gray-400">{fmtDT(g.granted_at)}</p>
                          </td>
                          <td className="px-2 py-1.5">
                            <button
                              onClick={() => doRevoke(g.user_id, g.name)}
                              data-testid={`revoke-${g.user_id}`}
                              className="text-[10px] bg-red-100 hover:bg-red-200 text-red-800 px-2 py-1 rounded"
                            >
                              Revoke
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Grant new access */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="px-3 py-2 border-b bg-purple-50 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-purple-900">+ Grant New Access</h3>
                {selectedGrant.size > 0 && (
                  <span className="bg-purple-600 text-white text-[10px] px-2 py-0.5 rounded-full">{selectedGrant.size} selected</span>
                )}
              </div>
              <div className="p-3 space-y-2 border-b">
                <div className="flex gap-1">
                  <input
                    type="text"
                    value={accessSearchTerm}
                    onChange={(e) => setAccessSearchTerm(e.target.value)}
                    placeholder="Search user by name/email/role..."
                    className="flex-1 border rounded px-3 py-1.5 text-sm"
                    data-testid="access-search"
                  />
                  <button
                    onClick={() => searchAccessUsers(accessSearchTerm)}
                    className="bg-purple-600 text-white text-xs px-3 py-1.5 rounded"
                  >
                    Search
                  </button>
                </div>
                <input
                  type="text"
                  value={grantNote}
                  onChange={(e) => setGrantNote(e.target.value)}
                  placeholder="Grant note (optional, e.g. 'Delhi region ke liye')"
                  className="w-full border rounded px-3 py-1.5 text-xs"
                  data-testid="access-note"
                />
              </div>
              <div className="max-h-[380px] overflow-y-auto">
                {accessSearchUsers.length === 0 ? (
                  <p className="p-6 text-center text-sm text-gray-500">Sab eligible users ko already access mil chuka hai — ya search results nahi hain.</p>
                ) : (
                  <div className="divide-y">
                    {accessSearchUsers.map((u) => {
                      const isSel = selectedGrant.has(u.id);
                      return (
                        <label
                          key={u.id}
                          className={`p-2 flex items-center gap-2 cursor-pointer hover:bg-purple-50 ${isSel ? 'bg-purple-100' : ''}`}
                          data-testid={`access-picker-${u.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={isSel}
                            onChange={() => toggleGrant(u.id)}
                            className="cursor-pointer"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">{u.name}</p>
                            <p className="text-[10px] text-gray-500">{u.email} · <span className="uppercase">{u.role}</span></p>
                          </div>
                          <span className="text-xs text-gray-500">{fmt(u.wallet_balance || 0)}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="p-3 border-t bg-slate-50">
                <button
                  onClick={doGrantSelected}
                  disabled={selectedGrant.size === 0}
                  data-testid="access-grant-btn"
                  className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white text-sm font-medium px-4 py-2 rounded"
                >
                  Grant BBPS Access to {selectedGrant.size} User{selectedGrant.size !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Integration Guide tab */}
        {tab === 'integration' && (
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-6" data-testid="bbps-integration-guide">
            <div>
              <h3 className="text-lg font-bold text-gray-900">🔌 Real BBPS Aggregator Plug-in Guide</h3>
              <p className="text-sm text-gray-600 mt-1">Jab aapko Setu / EuroNet / Cashfree / Instantpay se BBPS API approval mil jaye, in steps se real API integrate karein.</p>
            </div>

            <section className="border rounded-lg overflow-hidden">
              <div className="bg-blue-50 px-3 py-2 border-b text-sm font-semibold text-blue-900">Step 1 — Environment Variables</div>
              <div className="p-3 space-y-2 text-xs">
                <p className="text-gray-700">Add these keys to <code className="bg-gray-100 px-1 py-0.5 rounded font-mono">/app/backend/.env</code>:</p>
                <pre className="bg-gray-900 text-emerald-300 p-3 rounded overflow-x-auto text-[11px] font-mono">
{`# BBPS aggregator (choose one provider)
BBPS_PROVIDER=setu              # setu | euronet | cashfree | instantpay
BBPS_API_BASE=https://api.setu.co/bbps
BBPS_API_KEY=your_api_key_here
BBPS_API_SECRET=your_api_secret_here
BBPS_MERCHANT_ID=your_merchant_id
BBPS_AGENT_ID=your_agent_id     # provided by aggregator
BBPS_MODE=sandbox               # sandbox | production`}
                </pre>
              </div>
            </section>

            <section className="border rounded-lg overflow-hidden">
              <div className="bg-purple-50 px-3 py-2 border-b text-sm font-semibold text-purple-900">Step 2 — Swap the 2 Mock Functions</div>
              <div className="p-3 space-y-2 text-xs">
                <p className="text-gray-700">Open <code className="bg-gray-100 px-1 py-0.5 rounded font-mono">/app/backend/routes/bbps.py</code> and replace these two functions:</p>
                <pre className="bg-gray-900 text-emerald-300 p-3 rounded overflow-x-auto text-[11px] font-mono">
{`import httpx, os

BBPS_BASE = os.environ["BBPS_API_BASE"]
BBPS_KEY  = os.environ["BBPS_API_KEY"]

def _mock_fetch_bill(biller_id, customer_id):
    r = httpx.post(f"{BBPS_BASE}/fetchBill",
        headers={"X-Api-Key": BBPS_KEY},
        json={"biller_id": biller_id,
              "customer_params": {"customer_id": customer_id}})
    return r.json()  # response shape already matches!

def _mock_pay_bill(biller_id, customer_id, amount):
    r = httpx.post(f"{BBPS_BASE}/payBill",
        headers={"X-Api-Key": BBPS_KEY},
        json={"biller_id": biller_id,
              "customer_id": customer_id,
              "amount": amount})
    return r.json()`}
                </pre>
                <p className="text-gray-600 mt-2">✅ Response schemas already NPCI BBPS-compatible — no other file change needed. Frontend, wallet, ledger sab wahi ka wahi chalega.</p>
              </div>
            </section>

            <section className="border rounded-lg overflow-hidden">
              <div className="bg-emerald-50 px-3 py-2 border-b text-sm font-semibold text-emerald-900">Step 3 — Biller Directory Sync</div>
              <div className="p-3 space-y-2 text-xs">
                <p className="text-gray-700">Real BBPS ka biller list dynamic hoti hai. Aggregator ke <code className="bg-gray-100 px-1 py-0.5 rounded font-mono">GET /billers</code> endpoint se biller_id fetch karke aap hard-coded <code className="bg-gray-100 px-1 py-0.5 rounded font-mono">BILLER_DIRECTORY</code> ki jagah database-backed <code className="bg-gray-100 px-1 py-0.5 rounded font-mono">bbps_billers</code> collection use karein. Ek nightly cron endpoint bana lein.</p>
                <p className="text-gray-600">Real BBPS biller_ids format: <code className="bg-gray-100 px-1 py-0.5 rounded font-mono">INDANE00NAT00</code>, <code className="bg-gray-100 px-1 py-0.5 rounded font-mono">BHARATGAS00NAT00</code>, <code className="bg-gray-100 px-1 py-0.5 rounded font-mono">HPCL00NAT00</code>, etc.</p>
              </div>
            </section>

            <section className="border rounded-lg overflow-hidden">
              <div className="bg-amber-50 px-3 py-2 border-b text-sm font-semibold text-amber-900">Step 4 — Sandbox → Production Checklist</div>
              <ul className="p-3 space-y-1 text-xs list-disc list-inside text-gray-700">
                <li>Sandbox me 100+ test transactions run karke every biller_id validate karein</li>
                <li>NPCI se live BBPOU/COU certification lein (agent ID + BBPOU ID milega)</li>
                <li><code className="bg-gray-100 px-1 py-0.5 rounded font-mono">BBPS_MODE=production</code> aur real BBPS_API_BASE switch karein</li>
                <li>Idempotency-key har request pe zaroor bhejein (retries safe rahenge)</li>
                <li>Bank prepaid deposit → aggregator credit → yahan Admin panel se <b>Load API Balance</b> se reflect karein</li>
                <li>Refund/dispute workflow: <code className="bg-gray-100 px-1 py-0.5 rounded font-mono">POST /raiseComplaint</code> endpoint add karein</li>
              </ul>
            </section>

            <section className="border rounded-lg overflow-hidden">
              <div className="bg-rose-50 px-3 py-2 border-b text-sm font-semibold text-rose-900">Recommended Aggregators (India)</div>
              <div className="p-3 space-y-2 text-xs">
                <table className="w-full">
                  <thead className="bg-slate-50 text-[10px] uppercase text-gray-600">
                    <tr><th className="text-left p-1.5">Aggregator</th><th className="text-left p-1.5">Best For</th><th className="text-left p-1.5">Onboarding Time</th></tr>
                  </thead>
                  <tbody>
                    <tr className="border-t"><td className="p-1.5 font-medium">Setu (bharatConnect)</td><td className="p-1.5">Modern REST API, dev-friendly</td><td className="p-1.5">2–4 weeks</td></tr>
                    <tr className="border-t"><td className="p-1.5 font-medium">EuroNet</td><td className="p-1.5">Pan-India coverage, mature</td><td className="p-1.5">4–6 weeks</td></tr>
                    <tr className="border-t"><td className="p-1.5 font-medium">Cashfree</td><td className="p-1.5">Fintech ecosystem</td><td className="p-1.5">3–5 weeks</td></tr>
                    <tr className="border-t"><td className="p-1.5 font-medium">Instantpay / Paysprint</td><td className="p-1.5">Agent-focused, fast approval</td><td className="p-1.5">1–2 weeks</td></tr>
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        )}
      </div>

      {/* Wallet Topup Modal */}
      {wtOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="wt-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold">Credit User Wallet</h3>
              <button onClick={() => setWtOpen(false)} className="text-gray-500 hover:text-gray-800">✕</button>
            </div>
            {!wtUserId && (
              <div className="space-y-2">
                <div className="flex gap-1">
                  <input
                    type="text"
                    value={wtSearch}
                    onChange={(e) => setWtSearch(e.target.value)}
                    placeholder="Search user..."
                    className="flex-1 border rounded px-3 py-1.5 text-sm"
                    data-testid="wt-search"
                  />
                  <button onClick={doSearchUsers} className="bg-purple-600 text-white px-3 py-1.5 rounded text-sm">Search</button>
                </div>
                <div className="max-h-52 overflow-y-auto border rounded">
                  {wtUsers.map((u) => (
                    <button
                      key={u.id}
                      onClick={() => setWtUserId(u.id)}
                      data-testid={`wt-user-${u.id}`}
                      className="w-full text-left p-2 hover:bg-purple-50 border-b text-sm flex justify-between items-center"
                    >
                      <div>
                        <p className="font-medium">{u.name}</p>
                        <p className="text-[10px] text-gray-500">{u.email} · {u.role}</p>
                      </div>
                      <span className="text-xs text-gray-600">{fmt(u.wallet_balance)}</span>
                    </button>
                  ))}
                  {wtUsers.length === 0 && <p className="p-3 text-xs text-gray-500 text-center">No users</p>}
                </div>
              </div>
            )}
            {wtUserId && (
              <>
                <div className="bg-purple-50 border border-purple-200 rounded px-3 py-2 text-xs flex justify-between items-center">
                  <span>User: <strong>{wtUsers.find(u => u.id === wtUserId)?.name || wtUserId}</strong></span>
                  <button onClick={() => setWtUserId('')} className="text-purple-700 hover:underline text-[11px]">Change</button>
                </div>
                <div>
                  <label className="text-xs text-gray-600">Amount</label>
                  <input
                    type="number"
                    value={wtAmount}
                    onChange={(e) => setWtAmount(e.target.value)}
                    placeholder="₹ Amount"
                    className="w-full border rounded px-3 py-2 text-lg font-bold"
                    data-testid="wt-amount"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Note (optional)</label>
                  <input
                    type="text"
                    value={wtNote}
                    onChange={(e) => setWtNote(e.target.value)}
                    placeholder="e.g. Advance topup"
                    className="w-full border rounded px-3 py-1.5 text-sm"
                    data-testid="wt-note"
                  />
                </div>
                <button
                  onClick={doWtSubmit}
                  disabled={wtBusy}
                  data-testid="wt-submit"
                  className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-4 py-2 rounded font-medium"
                >
                  {wtBusy ? 'Crediting...' : `Credit ${fmt(parseFloat(wtAmount) || 0)}`}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* API Topup Modal */}
      {apiOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="api-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold">Load BBPS API Balance</h3>
              <button onClick={() => setApiOpen(false)} className="text-gray-500 hover:text-gray-800">✕</button>
            </div>
            <p className="text-xs text-gray-600 bg-yellow-50 border border-yellow-200 rounded p-2">Mock top-up. Real aggregator me pehle bank transfer / prepaid deposit karke fir yahaan credit reflect kar dena.</p>
            <div>
              <label className="text-xs text-gray-600">Amount</label>
              <input type="number" value={apiAmount} onChange={(e) => setApiAmount(e.target.value)} placeholder="₹ Amount" className="w-full border rounded px-3 py-2 text-lg font-bold" data-testid="api-amount" />
            </div>
            <div>
              <label className="text-xs text-gray-600">Note</label>
              <input type="text" value={apiNote} onChange={(e) => setApiNote(e.target.value)} placeholder="e.g. HDFC UTR 12345" className="w-full border rounded px-3 py-1.5 text-sm" data-testid="api-note" />
            </div>
            <button onClick={doApiTopup} disabled={apiBusy} data-testid="api-submit" className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-4 py-2 rounded font-medium">
              {apiBusy ? 'Loading...' : `Load ${fmt(parseFloat(apiAmount) || 0)}`}
            </button>
          </div>
        </div>
      )}

      {/* Drill-down Report Modal */}
      {reportKind && (
        <BbpsReportModal kind={reportKind} onClose={() => setReportKind(null)} />
      )}
    </div>
  );
}

function StatTile({ label, value, sub, color = 'blue', testid, onClick }) {
  const borderMap = { purple: '#a855f7', blue: '#3b82f6', emerald: '#10b981', orange: '#f97316' };
  const clickable = typeof onClick === 'function';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      className={`bg-white rounded-xl shadow-sm p-4 border-l-4 text-left w-full transition ${clickable ? 'hover:shadow-lg hover:-translate-y-0.5 cursor-pointer active:translate-y-0' : 'cursor-default'}`}
      style={{ borderLeftColor: borderMap[color] || '#3b82f6' }}
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase text-gray-500 font-medium tracking-widest">{label}</p>
        {clickable && <span className="text-[10px] text-purple-600 font-semibold">↗ Details</span>}
      </div>
      <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
      {sub && <p className="text-[10px] text-gray-500 mt-1">{sub}</p>}
    </button>
  );
}

function MetricRow({ label, value, bold }) {
  return (
    <div className="flex justify-between border-b pb-1.5">
      <span className="text-gray-600 text-xs">{label}</span>
      <span className={bold ? 'text-lg font-bold text-gray-900' : 'text-gray-900'}>{value}</span>
    </div>
  );
}
